import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FC, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { merge } from '../../helpers/Utility';
import { pageType } from '../../services/authentication/routes/AuthenticatedRoute';
import { ConfirmedLink } from '../shared/link/ConfirmedLink';
import pagesTranslations from '@/pages/pages.translations';

const isPageActive = (pathname: string, page: pageType): boolean => {
  if (page.children && page.children.length > 0) {
    return page.children.some(child => isPageActive(pathname, child));
  }

  if (page.to === '/team') {
    return pathname === '/team' || pathname.startsWith('/team-member');
  }

  return page.to === '/' ? pathname === '/' : pathname === page.to || pathname.startsWith(`${page.to}/`);
};

interface HeaderSubmenuLinkProps {
  page: pageType;
}

export const HeaderSubmenuLink: FC<HeaderSubmenuLinkProps> = ({ page }) => {
  const location = useLocation();
  const isActive = isPageActive(location.pathname, page);

  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  return (
    <Popover
      as="div"
      className="relative inline-flex h-full"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <PopoverButton
        className={`inline-flex h-full items-center gap-1 border-b-2 px-1 pt-1 text-sm font-medium ${
          isActive ? 'border-primary-500 text-primary-std' : 'border-transparent text-secondary-std'
        }`}
        style={{ outline: 0 }}
        onClick={
          page.children
            ? () => {
                setOpen(false);
                navigate(page.children![0].to);
              }
            : undefined
        }
      >
        <span>{page.name}</span>
      </PopoverButton>
      {open && (
        <PopoverPanel
          static
          className="absolute left-0 top-full z-20 w-40 overflow-hidden rounded-md border bg-primary-std shadow-lg"
        >
          {page.children?.map(child => (
            <ConfirmedLink
              key={child.to}
              to={child.to}
              className={'block px-4 py-3 text-sm text-secondary-std hover:bg-secondary-std hover:text-primary-std'}
              onNavigate={() => setOpen(false)}
            >
              {child.name}
            </ConfirmedLink>
          ))}
        </PopoverPanel>
      )}
    </Popover>
  );
};

export const HeaderMobileSubmenuGroup: FC<HeaderSubmenuLinkProps> = ({ page }) => {
  const location = useLocation();
  const pageIsActive = isPageActive(location.pathname, page);
  const [mobileGroupOpen, setMobileGroupOpen] = useState(pageIsActive);

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileGroupOpen((open: boolean) => !open)}
        className={`w-full border-l-4 pl-3 pr-4 py-4 text-base font-medium ${
          pageIsActive
            ? 'border-transparent bg-secondary-std text-primary-std'
            : 'border-transparent text-secondary-std hover:bg-secondary-std'
        }`}
      >
        <span className="flex w-full items-center justify-between">
          <span>{page.name}</span>
          <FiChevronDown className={`h-4 w-4 transition-transform ${mobileGroupOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {mobileGroupOpen && (
        <div className="bg-secondary-std bg-opacity-20">
          {page.children?.map(child => {
            const isActive = isPageActive(location.pathname, child);

            return (
              <ConfirmedLink
                key={`${child.name}-${child.to}-mobile-submenu`}
                to={child.to}
                className={`block border-l-4 py-3 pl-8 pr-4 text-sm font-medium ${
                  isActive
                    ? 'border-primary-500 text-primary-std'
                    : 'border-transparent text-secondary-std hover:bg-secondary-std'
                }`}
              >
                {child.name}
              </ConfirmedLink>
            );
          })}
        </div>
      )}
    </>
  );
};
